import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  GripVertical,
  Trash2,
  Loader2,
  Link as LinkIcon,
  Minus,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import _ from 'lodash';
import { PageHeader } from '../../components/page-header';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';
import useOrgData from '../../../application/hooks/useOrgData';
import useKPIs from '../../../application/hooks/useKPIs';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
};

// ─── Constants ──────────────────────────────────────────────────────────────

const MAX_KPIS = 6;
const MIN_KPIS = 1;

// ─── Types ──────────────────────────────────────────────────────────────────

interface WidgetTile {
  key: number;
  title: string;
  fullWidth: boolean;
  type: string;
  widgetOptions?: Record<string, any>;
}

interface DashboardStatsConfig {
  selectedKPIs: string[];
  kpiStatsEnabled: boolean;
  recentDealsEnabled: boolean;
  recentContactsEnabled: boolean;
  dashboardConfig?: WidgetTile[];
}

// ─── Widget Metadata ────────────────────────────────────────────────────────

const WIDGET_META: Record<
  string,
  {
    title: string;
    description: string;
    action: string;
    route?: string;
    defaultFullWidth: boolean;
  }
> = {
  pronto: {
    title: 'Pronto',
    description: 'Deep link to Pronto messaging app',
    action: 'Open Pronto',
    route: '/pronto',
    defaultFullWidth: false,
  },
  feed: {
    title: 'Feed',
    description: 'Deep link to the activity feed',
    action: 'Open Feed',
    route: '/feed',
    defaultFullWidth: false,
  },
  appointment: {
    title: 'Appointments',
    description: 'Upcoming appointments list',
    action: 'View Appointments',
    defaultFullWidth: true,
  },
  userTask: {
    title: 'My Tasks',
    description: 'User task list widget',
    action: 'View Tasks',
    defaultFullWidth: true,
  },
  personalStats: {
    title: 'My Stats (KPIs)',
    description: 'Personal KPI stats with Today/This Month',
    action: 'View Stats',
    defaultFullWidth: true,
  },
  recentDeals: {
    title: 'Recent Deals',
    description: 'Carousel of recent deals',
    action: 'View Deals',
    defaultFullWidth: true,
  },
  webLink: {
    title: 'Web Link',
    description: 'Custom web link tile',
    action: 'Open Link',
    defaultFullWidth: false,
  },
  spacer: {
    title: 'Spacer',
    description: 'Vertical space between widgets',
    action: '',
    defaultFullWidth: true,
  },
};

const AVAILABLE_WIDGET_TYPES = Object.keys(WIDGET_META);

const DEFAULT_TILES: WidgetTile[] = [
  { key: 2, title: 'Pronto', fullWidth: false, type: 'pronto' },
  { key: 3, title: 'Feed', fullWidth: false, type: 'feed' },
  {
    key: 4,
    title: 'Appointments',
    fullWidth: true,
    type: 'appointment',
    widgetOptions: { showPast: false, daysAhead: 7 },
  },
  {
    key: 5,
    title: 'My Tasks',
    fullWidth: true,
    type: 'userTask',
    widgetOptions: { showCompleted: false, maxTasks: 5 },
  },
];

const defaultConfig: DashboardStatsConfig = {
  selectedKPIs: [],
  kpiStatsEnabled: true,
  recentDealsEnabled: true,
  recentContactsEnabled: true,
  dashboardConfig: undefined,
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const mixedSizeCollision: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  return closestCenter(args);
};

function migrateConfig(raw: DashboardStatsConfig): DashboardStatsConfig {
  const config = _.cloneDeep(raw);
  if (!config.dashboardConfig) {
    const tiles = _.cloneDeep(DEFAULT_TILES);
    config.dashboardConfig = tiles.filter((t) => {
      if (t.type === 'personalStats' && config.kpiStatsEnabled === false) {
        return false;
      }
      if (t.type === 'recentDeals' && config.recentDealsEnabled === false) {
        return false;
      }
      return true;
    });
  }
  config.kpiStatsEnabled = config.dashboardConfig.some(
    (t) => t.type === 'personalStats'
  );
  config.recentDealsEnabled = config.dashboardConfig.some(
    (t) => t.type === 'recentDeals'
  );
  return config;
}

// ─── Sortable Tile Card ─────────────────────────────────────────────────────

const SortableTileCard = React.memo(function SortableTileCard({
  tile,
  onRemove,
  children,
}: {
  tile: WidgetTile;
  onRemove: (key: number) => void;
  children?: React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tile.key });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const meta = WIDGET_META[tile.type];

  if (tile.type === 'spacer') {
    return (
      <div ref={setNodeRef} style={style}>
        <div className="flex items-center p-2 rounded-lg border border-dashed border-border">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing flex items-center text-muted-foreground mr-2"
          >
            <GripVertical className="size-4" />
          </button>
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground mx-2">spacer</span>
          <div className="flex-1 h-px bg-border" />
          <button
            type="button"
            onClick={() => onRemove(tile.key)}
            className="text-muted-foreground hover:text-destructive ml-2 p-0.5"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex flex-col p-3 rounded-xl bg-muted/50 border border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            <button
              type="button"
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing flex items-center text-muted-foreground"
            >
              <GripVertical className="size-4" />
            </button>
            <span className="text-sm font-semibold text-foreground truncate">
              {meta?.title || tile.title}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {meta?.route && (
              <span className="text-xs text-muted-foreground mr-1">
                {meta.route}
              </span>
            )}
            <button
              type="button"
              onClick={() => onRemove(tile.key)}
              className="text-muted-foreground hover:text-destructive p-0.5"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
});

// ─── Web Link Inline Config ─────────────────────────────────────────────────

const WebLinkConfig = React.memo(function WebLinkConfig({
  tile,
  onUpdateOptions,
}: {
  tile: WidgetTile;
  onUpdateOptions: (key: number, options: Record<string, any>) => void;
}) {
  const url = tile.widgetOptions?.url || '';
  const label = tile.widgetOptions?.label || '';

  return (
    <div className="mt-2 ml-6 space-y-2">
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">URL</label>
        <Input
          placeholder="https://example.com"
          value={url}
          onChange={(e) => {
            onUpdateOptions(tile.key, {
              ...tile.widgetOptions,
              url: e.target.value,
              title: tile.widgetOptions?.label || '',
            });
          }}
          className="h-8 text-sm"
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">
          Label
        </label>
        <Input
          placeholder="Button label"
          value={label}
          onChange={(e) => {
            onUpdateOptions(tile.key, {
              ...tile.widgetOptions,
              label: e.target.value,
              title: e.target.value,
            });
          }}
          className="h-8 text-sm"
        />
      </div>
    </div>
  );
});

// ─── KPI Inline Config ──────────────────────────────────────────────────────

const KpiInlineConfig = React.memo(function KpiInlineConfig({
  selectedKPIs,
  KPIs,
  kpisLoading,
  onChange,
}: {
  selectedKPIs: string[];
  KPIs: any[];
  kpisLoading: boolean;
  onChange: (ids: string[]) => void;
}) {
  const availableKPIs = useMemo(
    () => KPIs.filter((kpi) => !kpi.isHidden),
    [KPIs]
  );

  if (kpisLoading) {
    return (
      <p className="text-xs text-muted-foreground mt-2 ml-6">Loading KPIs...</p>
    );
  }

  if (availableKPIs.length === 0) {
    return (
      <p className="text-xs text-muted-foreground mt-2 ml-6">
        No KPI rules found.
      </p>
    );
  }

  const handleToggleKpi = (kpiId: string) => {
    if (selectedKPIs.includes(kpiId)) {
      onChange(selectedKPIs.filter((id) => id !== kpiId));
    } else {
      if (selectedKPIs.length >= MAX_KPIS) {
        return;
      }
      onChange([...selectedKPIs, kpiId]);
    }
  };

  return (
    <div className="mt-2 ml-6">
      <p className="text-xs text-muted-foreground mb-2">
        Select {MIN_KPIS}-{MAX_KPIS} KPIs to display:
      </p>
      <div className="flex flex-wrap gap-1.5">
        {availableKPIs.map((kpi) => {
          const isSelected = selectedKPIs.includes(kpi.id || '');
          return (
            <button
              key={kpi.id}
              type="button"
              onClick={() => handleToggleKpi(kpi.id)}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium border transition-colors',
                isSelected
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted'
              )}
            >
              {kpi.title}
            </button>
          );
        })}
      </div>
      {selectedKPIs.length > 0 && (
        <p className="text-xs text-muted-foreground mt-1.5">
          {selectedKPIs.length}/{MAX_KPIS} selected
        </p>
      )}
    </div>
  );
});

// ─── Preview Panel ──────────────────────────────────────────────────────────

const PreviewPanel = React.memo(function PreviewPanel({
  tiles,
  selectedKPIs,
  KPIs,
}: {
  tiles: WidgetTile[];
  selectedKPIs: string[];
  KPIs: { id?: string; title?: string }[];
}) {
  return (
    <div className="rounded-2xl border border-border bg-muted/30 p-5 sticky top-4">
      <h3 className="font-bold text-lg text-foreground mb-0.5">Live Preview</h3>
      <p className="text-sm text-muted-foreground mb-4">
        This is how the mobile dashboard will look.
      </p>

      <div className="flex flex-wrap gap-2">
        {tiles.map((tile) => {
          const meta = WIDGET_META[tile.type];

          if (tile.type === 'spacer') {
            return <div key={tile.key} className="w-full h-4" />;
          }

          if (tile.type === 'personalStats') {
            const selectedKpiTitles = selectedKPIs
              .map((id) => KPIs.find((k) => k.id === id)?.title)
              .filter(Boolean);
            return (
              <div
                key={tile.key}
                className="w-full rounded-xl border border-border bg-card p-3"
              >
                <p className="text-sm font-semibold text-foreground mb-1">
                  My Stats
                </p>
                {selectedKpiTitles.length > 0 ? (
                  selectedKpiTitles.slice(0, 3).map((title) => (
                    <div key={title} className="flex justify-between py-0.5">
                      <span className="text-xs text-muted-foreground">
                        {title}
                      </span>
                      <span className="text-xs font-medium text-foreground">
                        $0
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No KPIs selected
                  </p>
                )}
              </div>
            );
          }

          if (tile.type === 'webLink') {
            const label = tile.widgetOptions?.label || 'Web Link';
            const url = tile.widgetOptions?.url;
            return (
              <div
                key={tile.key}
                className="w-[calc(50%-4px)] rounded-xl bg-primary p-3 flex flex-col gap-0.5"
              >
                <span className="text-sm font-semibold text-primary-foreground truncate">
                  {label}
                </span>
                <span className="text-xs text-primary-foreground/80 truncate">
                  {url || 'No URL configured'}
                </span>
              </div>
            );
          }

          if (!tile.fullWidth) {
            return (
              <div
                key={tile.key}
                className="w-[calc(50%-4px)] rounded-xl bg-primary p-3 flex flex-col gap-0.5"
              >
                <span className="text-sm font-semibold text-primary-foreground truncate">
                  {tile.title}
                </span>
                <span className="text-xs text-primary-foreground/80 truncate">
                  {meta?.action || tile.type}
                </span>
              </div>
            );
          }

          return (
            <div
              key={tile.key}
              className="w-full rounded-xl border border-border bg-card p-3"
            >
              <p className="text-sm font-semibold text-foreground">
                {tile.title}
              </p>
              <p className="text-xs text-muted-foreground">
                {meta?.description || tile.type}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
});

// ─── Main Component ─────────────────────────────────────────────────────────

export function DashboardConfigPage() {
  const { orgData, updateOrgData } = useOrgData();
  const { KPIs, isLoading: kpisLoading } = useKPIs();
  const [config, setConfig] = useState<DashboardStatsConfig>(defaultConfig);
  const [hasChanged, setHasChanged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();

  const savedConfig: DashboardStatsConfig =
    orgData?.configParms?.dashboardStats || defaultConfig;

  useEffect(() => {
    if (orgData) {
      const existing = orgData.configParms?.dashboardStats;
      if (existing) {
        setConfig(migrateConfig(existing));
      }
    }
  }, [orgData]);

  useEffect(() => {
    setHasChanged(!_.isEqual(config, savedConfig));
  }, [config, savedConfig]);

  const tiles: WidgetTile[] = config.dashboardConfig || DEFAULT_TILES;

  const setTiles = useCallback((newTiles: WidgetTile[]) => {
    setConfig((prev) => ({
      ...prev,
      dashboardConfig: newTiles,
      kpiStatsEnabled: newTiles.some((t) => t.type === 'personalStats'),
      recentDealsEnabled: newTiles.some((t) => t.type === 'recentDeals'),
    }));
  }, []);

  const handleSave = useCallback(async () => {
    const hasStats = tiles.some((t) => t.type === 'personalStats');
    if (hasStats && config.selectedKPIs.length < MIN_KPIS) {
      toast.error(`Please select at least ${MIN_KPIS} KPI`);
      return;
    }

    setIsSaving(true);
    try {
      const configToSave: DashboardStatsConfig = {
        ...config,
        kpiStatsEnabled: tiles.some((t) => t.type === 'personalStats'),
        recentDealsEnabled: tiles.some((t) => t.type === 'recentDeals'),
        recentContactsEnabled: config.recentContactsEnabled,
      };

      const newConfigParms = {
        ...orgData?.configParms,
        dashboardStats: configToSave,
      };
      const newOrgInput = {
        ...orgData,
        configParms: JSON.stringify(newConfigParms),
      };
      const success = await updateOrgData(newOrgInput);
      if (success) {
        toast.success('Dashboard configuration saved');
      } else {
        toast.error('Failed to save dashboard configuration');
      }
    } catch {
      toast.error('Failed to save dashboard configuration');
    } finally {
      setIsSaving(false);
    }
  }, [config, tiles, orgData, updateOrgData, toast]);

  const handleAddWidget = useCallback(
    (type: string) => {
      const widgetDef = WIDGET_META[type];
      if (!widgetDef) {
        return;
      }
      const nextKey =
        tiles.length > 0 ? Math.max(...tiles.map((t) => t.key)) + 1 : 1;
      const newTile: WidgetTile = {
        key: nextKey,
        title: widgetDef.title,
        fullWidth: widgetDef.defaultFullWidth,
        type,
      };
      setTiles([...tiles, newTile]);
    },
    [tiles, setTiles]
  );

  const handleRemoveByKey = useCallback(
    (key: number) => {
      setTiles(tiles.filter((t) => t.key !== key));
    },
    [tiles, setTiles]
  );

  const handleUpdateOptions = useCallback(
    (key: number, options: Record<string, any>) => {
      setTiles(
        tiles.map((t) => (t.key === key ? { ...t, widgetOptions: options } : t))
      );
    },
    [tiles, setTiles]
  );

  const handleKpiChange = useCallback((ids: string[]) => {
    setConfig((prev) => ({ ...prev, selectedKPIs: ids }));
  }, []);

  // DnD
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = tiles.findIndex((t) => t.key === active.id);
      const newIndex = tiles.findIndex((t) => t.key === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        setTiles(arrayMove(tiles, oldIndex, newIndex));
      }
    },
    [tiles, setTiles]
  );

  const addableWidgets = useMemo(
    () =>
      AVAILABLE_WIDGET_TYPES.filter(
        (type) =>
          type !== 'spacer' &&
          type !== 'webLink' &&
          !tiles.some((t) => t.type === type)
      ),
    [tiles]
  );

  const handleAddSpacer = useCallback(() => {
    const nextKey =
      tiles.length > 0 ? Math.max(...tiles.map((t) => t.key)) + 1 : 1;
    setTiles([
      ...tiles,
      { key: nextKey, title: 'Spacer', fullWidth: true, type: 'spacer' },
    ]);
  }, [tiles, setTiles]);

  const handleAddWebLink = useCallback(() => {
    const nextKey =
      tiles.length > 0 ? Math.max(...tiles.map((t) => t.key)) + 1 : 1;
    setTiles([
      ...tiles,
      {
        key: nextKey,
        title: 'Web Link',
        fullWidth: false,
        type: 'webLink',
        widgetOptions: { url: '', label: '', title: '' },
      },
    ]);
  }, [tiles, setTiles]);

  const tileIds = useMemo(() => tiles.map((t) => t.key), [tiles]);

  const [addWidgetValue, setAddWidgetValue] = useState<string>('');

  if (!orgData) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Dashboard"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Dashboard"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
        actions={[
          {
            id: 'save',
            label: isSaving ? 'Saving...' : 'Save',
            variant: 'primary',
            onClick: hasChanged ? handleSave : undefined,
          },
        ]}
      />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="max-w-6xl mx-auto">
          {/* Description */}
          <div className="mb-6">
            <h2 className="text-lg font-bold text-foreground">
              Dashboard Configuration
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Configure stats and layout for the mobile dashboard.
            </p>
          </div>

          {/* Two-column layout */}
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            {/* Left column - Layout Builder */}
            <div className="flex-1 min-w-0 w-full">
              <div className="border border-border rounded-xl p-4 sm:p-5">
                <h3 className="text-base font-semibold text-foreground mb-0.5">
                  Layout Builder
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Drag and drop to reorder widgets. Add or remove tiles to
                  customize the mobile dashboard.
                </p>

                <DndContext
                  sensors={sensors}
                  collisionDetection={mixedSizeCollision}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={tileIds}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="flex flex-wrap gap-2 mb-4">
                      {tiles.map((tile) => {
                        const isHalf =
                          !tile.fullWidth && tile.type !== 'spacer';
                        return (
                          <div
                            key={tile.key}
                            className={cn(
                              isHalf ? 'w-full sm:w-[calc(50%-4px)]' : 'w-full'
                            )}
                          >
                            <SortableTileCard
                              tile={tile}
                              onRemove={handleRemoveByKey}
                            >
                              {tile.type === 'personalStats' && (
                                <KpiInlineConfig
                                  selectedKPIs={config.selectedKPIs}
                                  KPIs={KPIs}
                                  kpisLoading={kpisLoading}
                                  onChange={handleKpiChange}
                                />
                              )}
                              {tile.type === 'webLink' && (
                                <WebLinkConfig
                                  tile={tile}
                                  onUpdateOptions={handleUpdateOptions}
                                />
                              )}
                            </SortableTileCard>
                          </div>
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>

                {/* Add Widget Select */}
                {addableWidgets.length > 0 && (
                  <Select
                    value={addWidgetValue}
                    onValueChange={(val) => {
                      handleAddWidget(val);
                      setAddWidgetValue('');
                    }}
                  >
                    <SelectTrigger className="border-dashed text-muted-foreground">
                      <SelectValue placeholder="+ Add Widget" />
                    </SelectTrigger>
                    <SelectContent>
                      {addableWidgets.map((type) => (
                        <SelectItem key={type} value={type}>
                          <div>
                            <span className="font-medium">
                              {WIDGET_META[type].title}
                            </span>
                            <span className="text-muted-foreground ml-2 text-xs">
                              {WIDGET_META[type].description}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Add Spacer / Web Link buttons */}
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={handleAddWebLink}
                    className="flex-1 flex items-center justify-center gap-1.5 p-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
                  >
                    <LinkIcon className="size-3.5" />
                    Add Web Link
                  </button>
                  <button
                    type="button"
                    onClick={handleAddSpacer}
                    className="flex-1 flex items-center justify-center gap-1.5 p-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
                  >
                    <Minus className="size-3.5" />
                    Add Spacer
                  </button>
                </div>
              </div>
            </div>

            {/* Right column - Live Preview */}
            <div className="w-full lg:w-[340px] shrink-0">
              <PreviewPanel
                tiles={tiles}
                selectedKPIs={config.selectedKPIs}
                KPIs={KPIs}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
